import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { IconPlayerRecord, IconPlayerStop } from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import { apiService, ApiTrafficEvent } from '../services/api';
import { storage } from '../utils/localStorage';
import { decodeJWT } from '../utils/jwt';

interface RecordedStep {
    type: 'navigate' | 'click' | 'input' | 'api';
    label?: string;
    // navigate
    path?: string;
    // click / input
    selector?: string;
    value?: any;
    /** Field value before the edit — replay alternates old/new so updates stay repeatable */
    oldValue?: any;
    cell?: number;
    // api
    method?: string;
    url?: string;
    status?: number;
    requestBody?: any;
    responseBody?: any;
}

/** API endpoints that are app plumbing, not scenario behaviour. */
const IGNORED_API = [/^\/test-scenarios/, /^\/mercure/, /^\/applications/, /^\/user-filters/];

/**
 * Admin-only floating test recorder. Captures clicks and inputs on elements
 * with a data-e2e attribute, route changes, and all API traffic (via the
 * apiService request observer), then saves the recording as a TestScenario.
 */
export function RecordingToolbar() {
    const location = useLocation();
    const [isRecording, setIsRecording] = useState(false);
    const [stepCount, setStepCount] = useState(0);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [savedOk, setSavedOk] = useState(false);

    const stepsRef = useRef<RecordedStep[]>([]);
    const startPathRef = useRef('');
    const recordingRef = useRef(false);
    /** Value each field had when first focused during this recording */
    const initialValuesRef = useRef(new Map<string, string>());

    const token = storage.getItem('token');
    const isAdmin = token ? decodeJWT(token)?.user?.admin === true : false;

    const pushStep = useCallback((step: RecordedStep) => {
        stepsRef.current.push(step);
        setStepCount(stepsRef.current.length);
    }, []);

    // Route changes while recording
    useEffect(() => {
        if (!recordingRef.current) return;
        const path = location.pathname + location.search;
        if (path !== startPathRef.current) {
            pushStep({ type: 'navigate', path, label: `Navigácia: ${location.pathname}` });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    // Click + input capture (document level, capture phase)
    useEffect(() => {
        if (!isRecording) return;

        const onClick = (e: MouseEvent) => {
            const target = (e.target as HTMLElement)?.closest?.('[data-e2e]') as HTMLElement | null;
            if (!target) return;
            const selector = target.getAttribute('data-e2e')!;
            if (selector.startsWith('recorder:')) return; // ignore the toolbar itself
            const label = (target.getAttribute('data-e2e-label') || target.textContent || '').trim().slice(0, 80);
            const step: RecordedStep = { type: 'click', selector, label: `Klik: ${label || selector}` };
            // Row clicks: remember WHICH cell was hit — replay must click the
            // same one (editable cells swallow clicks, others navigate).
            if (selector.startsWith('row:')) {
                const cell = (e.target as HTMLElement)?.closest?.('td') as HTMLTableCellElement | null;
                if (cell) {
                    step.cell = cell.cellIndex;
                }
            }
            pushStep(step);
        };

        const onChange = (e: Event) => {
            const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
            const host = target?.closest?.('[data-e2e]') as HTMLElement | null;
            if (!host) return;
            const selector = host.getAttribute('data-e2e')!;
            if (!selector.startsWith('field:') && !selector.startsWith('pagesize:')) return;

            let value: any = target.value;
            if ((target as HTMLInputElement).type === 'password') {
                return; // never record passwords
            }
            if ((target as HTMLInputElement).type === 'checkbox') {
                value = (target as HTMLInputElement).checked;
            }
            if ((target as HTMLInputElement).type === 'file') {
                value = null; // files are carried by the recorded API request body
            }

            // Same field edited repeatedly in a row → keep only the final value
            const fieldName = selector.split(':')[1] ?? selector;
            const oldValue = initialValuesRef.current.get(selector);
            const last = stepsRef.current[stepsRef.current.length - 1];
            if (last && last.type === 'input' && last.selector === selector) {
                last.value = value;
                last.label = `Pole ${fieldName} = ${value}`;
                return;
            }
            pushStep({ type: 'input', selector, value, oldValue, label: `Pole ${fieldName} = ${value}` });
        };

        const onFocusIn = (e: Event) => {
            const target = e.target as HTMLInputElement;
            const host = target?.closest?.('[data-e2e]') as HTMLElement | null;
            if (!host) return;
            const selector = host.getAttribute('data-e2e')!;
            if (!selector.startsWith('field:')) return;
            if (target.type === 'password' || target.type === 'file') return;
            if (!initialValuesRef.current.has(selector)) {
                initialValuesRef.current.set(selector, target.value);
            }
        };

        document.addEventListener('click', onClick, true);
        document.addEventListener('change', onChange, true);
        document.addEventListener('focusin', onFocusIn, true);
        return () => {
            document.removeEventListener('click', onClick, true);
            document.removeEventListener('change', onChange, true);
            document.removeEventListener('focusin', onFocusIn, true);
        };
    }, [isRecording, pushStep]);

    // API traffic capture
    useEffect(() => {
        if (!isRecording) {
            return;
        }
        apiService.setRequestObserver((event: ApiTrafficEvent) => {
            if (IGNORED_API.some((rx) => rx.test(event.url))) return;
            pushStep({
                type: 'api',
                method: event.method,
                url: event.url,
                status: event.status,
                requestBody: event.requestBody,
                responseBody: event.responseBody,
                label: `${event.method} ${event.url} → ${event.status}`,
            });
        });
        return () => apiService.setRequestObserver(null);
    }, [isRecording, pushStep]);

    if (!isAdmin) {
        return null;
    }

    const start = () => {
        stepsRef.current = [];
        initialValuesRef.current = new Map();
        startPathRef.current = location.pathname + location.search;
        recordingRef.current = true;
        setStepCount(0);
        setSavedOk(false);
        setIsRecording(true);
    };

    const stop = () => {
        recordingRef.current = false;
        setIsRecording(false);
        if (stepsRef.current.length > 0) {
            setSaveError(null);
            setShowSaveModal(true);
        }
    };

    const discard = () => {
        stepsRef.current = [];
        setStepCount(0);
        setShowSaveModal(false);
        setName('');
        setDescription('');
    };

    const save = async () => {
        if (!name.trim()) {
            setSaveError('Zadaj názov scenára.');
            return;
        }
        setSaving(true);
        setSaveError(null);
        const response = await apiService.saveTestScenario({
            displayName: name.trim(),
            description: description.trim() || undefined,
            startPath: startPathRef.current,
            steps: stepsRef.current,
        });
        setSaving(false);
        if (response.success) {
            discard();
            setSavedOk(true);
            setTimeout(() => setSavedOk(false), 4000);
        } else {
            setSaveError(response.message || 'Scenár sa nepodarilo uložiť.');
        }
    };

    return (
        <>
            <div
                // Recording a scenario is desk work, and on a phone the pill sat
                // over the form's own controls - so it stays on wide screens.
                className="d-none d-lg-flex align-items-center gap-2"
                style={{
                    position: 'fixed',
                    bottom: '1rem',
                    right: '1rem',
                    zIndex: 1080,
                }}
                data-e2e="recorder:toolbar"
            >
                {savedOk && <span className="badge bg-success">Scenár uložený ✓</span>}
                {isRecording ? (
                    <Button variant="danger" size="sm" onClick={stop} className="d-flex align-items-center shadow">
                        <IconPlayerStop size={16} className="me-1" />
                        Stop
                        <span className="badge bg-white text-danger ms-2">{stepCount}</span>
                    </Button>
                ) : (
                    <Button variant="outline-danger" size="sm" onClick={start} className="d-flex align-items-center shadow bg-white">
                        <IconPlayerRecord size={16} className="me-1" />
                        Nahrávať test
                    </Button>
                )}
            </div>

            <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Uložiť testovací scenár</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">Nahraných {stepCount} krokov od <code>{startPathRef.current}</code>.</p>
                    <Form.Group className="mb-3">
                        <Form.Label>Názov *</Form.Label>
                        <Form.Control
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="napr. Vytvorenie úlohy s prílohou"
                            autoFocus
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Popis</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Form.Group>
                    {saveError && <div className="alert alert-danger mt-3 mb-0">{saveError}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={discard} disabled={saving}>
                        Zahodiť nahrávku
                    </Button>
                    <Button variant="primary" onClick={save} disabled={saving}>
                        {saving ? 'Ukladám…' : 'Uložiť'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
